import {
	type API_UserOrganization,
	Currency,
	PlatformSubscriptionStatus,
	Role,
} from "@pilatestime/shared-types";
import { useQueries, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
	AlertTriangle,
	ArrowLeft,
	ArrowRight,
	Building2,
	Info,
	Mail,
	ShieldCheck,
	Trash2,
	X,
} from "lucide-react";
import { useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
	fetchUserOrganizationsQueryOptions,
	useCreateOrganization,
	useDeleteOrganization,
} from "@/api/organizations";
import { fetchPlatformSubscriptionQueryOptions } from "@/api/platformBilling";
import { Badge } from "@/components/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useApiParameters } from "@/hooks/useApiParameters";
import { getOrganizationRoleLabel } from "@/utils/roleUtils";
import { cn } from "@/utils/utils";
import { AppBaseLayout } from "./_app/-components/AppBaseLayout";
import { OnboardingModal } from "./-components/OnboardingModal";

const searchSchema = z.object({
	skipAutoRedirect: z.boolean().catch(false),
});

const EXPLAINER_OPEN_KEY = "pilatestime:org-picker-explainer-open";

function useOrgExplainer() {
	const [isOpen, setIsOpen] = useState<boolean>(() => {
		if (typeof window === "undefined") return false;
		return window.localStorage.getItem(EXPLAINER_OPEN_KEY) === "1";
	});

	useEffect(() => {
		if (typeof window === "undefined") return;
		if (isOpen) window.localStorage.setItem(EXPLAINER_OPEN_KEY, "1");
		else window.localStorage.removeItem(EXPLAINER_OPEN_KEY);
	}, [isOpen]);

	return {
		isOpen,
		open: () => setIsOpen(true),
		close: () => setIsOpen(false),
	};
}

type OrgSub = {
	status: PlatformSubscriptionStatus | null;
	planName: string | null;
	trialEndsAt: string | null;
	isLoading: boolean;
	isError: boolean;
};

// Fetches the platform subscription for each owned organization so the card can
// surface its current plan and billing status. Scoped to owned orgs only: billing
// is an owner concern and the endpoint is only guaranteed to be readable by owners.
function useOrgSubscriptions(
	userId: string,
	orgs: API_UserOrganization[],
): Record<string, OrgSub> {
	const results = useQueries({
		queries: orgs.map((org) => ({
			...fetchPlatformSubscriptionQueryOptions(userId, org.id),
			staleTime: 60_000,
			retry: 1,
		})),
	});

	return useMemo(() => {
		const map: Record<string, OrgSub> = {};
		orgs.forEach((org, i) => {
			const r = results[i];
			map[org.id] = {
				status: r?.data?.subscription?.status ?? null,
				planName: r?.data?.plan?.name ?? null,
				trialEndsAt: r?.data?.subscription?.trialEndsAt ?? null,
				isLoading: r?.isPending ?? true,
				isError: r?.isError ?? false,
			};
		});
		return map;
	}, [orgs, results]);
}

type StatusKind =
	| "checking"
	| "active"
	| "trialing"
	| "setup_required"
	| "payment_required"
	| "past_due"
	| "inactive"
	| "unknown";

function deriveStatusKind(sub: OrgSub): StatusKind {
	if (sub.isLoading) return "checking";
	if (sub.isError) return "unknown";
	switch (sub.status) {
		case PlatformSubscriptionStatus.ACTIVE:
			return "active";
		case PlatformSubscriptionStatus.TRIALING:
			return "trialing";
		case PlatformSubscriptionStatus.PAST_DUE:
			return "past_due";
		case PlatformSubscriptionStatus.UNPAID:
			return "payment_required";
		case PlatformSubscriptionStatus.INCOMPLETE:
			return "setup_required";
		case PlatformSubscriptionStatus.INCOMPLETE_EXPIRED:
		case PlatformSubscriptionStatus.CANCELED:
		case PlatformSubscriptionStatus.PAUSED:
			return "inactive";
		case null:
			// No active subscription yet — the owner still needs to pick a plan.
			return "setup_required";
		default:
			return "unknown";
	}
}

function trialDaysLeft(trialEndsAt: string | null): number | null {
	if (!trialEndsAt) return null;
	const end = new Date(trialEndsAt).getTime();
	if (Number.isNaN(end)) return null;
	const days = Math.ceil((end - Date.now()) / (1000 * 60 * 60 * 24));
	return days > 0 ? days : null;
}

export const Route = createFileRoute("/_auth/app/select-organization")({
	validateSearch: searchSchema,
	loader: async ({ context }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		context.queryClient.ensureQueryData(
			fetchUserOrganizationsQueryOptions(userId, token),
		);
		return { userId, token };
	},
	component: SelectOrgPage,
});

function SelectOrgPage() {
	const { userId, token } = Route.useLoaderData();
	const { skipAutoRedirect } = Route.useSearch();
	const { t } = useTranslation();
	const navigate = useNavigate();

	const { data: organizations } = useSuspenseQuery(
		fetchUserOrganizationsQueryOptions(userId, token),
	);

	const ownedOrgs = useMemo(
		() => organizations.filter((o) => o.role === Role.ORG_OWNER),
		[organizations],
	);
	const invitedOrgs = useMemo(
		() => organizations.filter((o) => o.role !== Role.ORG_OWNER),
		[organizations],
	);

	const subscriptions = useOrgSubscriptions(userId, ownedOrgs);

	// Auto-redirect to the only organization if user has exactly one and skipAutoRedirect is false
	useEffect(() => {
		if (!skipAutoRedirect && organizations.length === 1) {
			navigate({
				to: "/app/org/$orgId",
				params: { orgId: organizations[0].id },
				replace: true,
			});
		}
	}, [skipAutoRedirect, organizations, navigate]);

	// Create Organization Dialog state
	// Two-step flow: "explainer" → "form" → null (closed)
	const [createStep, setCreateStep] = useState<"explainer" | "form" | null>(
		null,
	);
	const [newOrgName, setNewOrgName] = useState("");
	const [newOrgDescription, setNewOrgDescription] = useState("");
	const [newOrgCurrency, setNewOrgCurrency] = useState<Currency>(Currency.BRL);
	const createOrganizationMutation = useCreateOrganization();
	const deleteOrganizationMutation = useDeleteOrganization();
	const [orgToDelete, setOrgToDelete] = useState<API_UserOrganization | null>(
		null,
	);
	const [deleteOrgNameConfirm, setDeleteOrgNameConfirm] = useState("");

	// Generate unique IDs for form fields
	const orgNameId = useId();
	const orgDescriptionId = useId();
	const orgCurrencyId = useId();
	const confirmOrgNameId = useId();

	// Onboarding Modal state
	const [isOnboardingOpen, setIsOnboardingOpen] = useState(false);
	const [createdOrg, setCreatedOrg] = useState<{
		id: string;
		name: string;
	} | null>(null);

	const handleCreateOrganization = async () => {
		if (!newOrgName.trim()) return;
		try {
			const result = await createOrganizationMutation.mutateAsync({
				token,
				data: {
					name: newOrgName.trim(),
					description: newOrgDescription.trim() || undefined,
					defaultCurrency: newOrgCurrency,
				},
			});
			// Store created org info for onboarding
			setCreatedOrg({ id: result.id, name: result.name });
			// Close create flow and open onboarding
			setCreateStep(null);
			setNewOrgName("");
			setNewOrgDescription("");
			setNewOrgCurrency(Currency.BRL);
			setIsOnboardingOpen(true);
		} catch (error) {
			console.error("Failed to create organization:", error);
		}
	};

	const handleCloseCreateFlow = () => {
		setCreateStep(null);
	};

	const handleOpenDeleteDialog = (org: API_UserOrganization) => {
		setOrgToDelete(org);
		setDeleteOrgNameConfirm("");
	};

	const handleCloseDeleteDialog = () => {
		setOrgToDelete(null);
		setDeleteOrgNameConfirm("");
	};

	const handleConfirmDeleteOrganization = async () => {
		if (!orgToDelete) return;
		try {
			await deleteOrganizationMutation.mutateAsync({
				token,
				organizationId: orgToDelete.id,
			});
			setOrgToDelete(null);
			setDeleteOrgNameConfirm("");
		} catch (error) {
			// Error feedback is handled by toast system
			console.error("Failed to delete organization:", error);
		}
	};

	const hasAnyOrgs = organizations.length > 0;
	const explainer = useOrgExplainer();

	return (
		<AppBaseLayout>
			<div className="h-full overflow-y-auto bg-base-100">
				<div className="max-w-5xl mx-auto space-y-6 sm:space-y-8 py-6 sm:py-10 px-3 sm:px-4">
					<PageHeader
						hasAnyOrgs={hasAnyOrgs}
						onCreate={() => setCreateStep("explainer")}
						showExplainerTrigger={hasAnyOrgs && !explainer.isOpen}
						onOpenExplainer={explainer.open}
					/>

					{hasAnyOrgs && explainer.isOpen && (
						<OrgExplainerPanel onClose={explainer.close} />
					)}

					<CreateOrganizationFlow
						step={createStep}
						onStepChange={setCreateStep}
						onClose={handleCloseCreateFlow}
						orgNameId={orgNameId}
						orgDescriptionId={orgDescriptionId}
						orgCurrencyId={orgCurrencyId}
						name={newOrgName}
						description={newOrgDescription}
						currency={newOrgCurrency}
						onNameChange={setNewOrgName}
						onDescriptionChange={setNewOrgDescription}
						onCurrencyChange={setNewOrgCurrency}
						onSubmit={handleCreateOrganization}
						isSubmitting={createOrganizationMutation.isPending}
					/>

					{!hasAnyOrgs ? (
						<EmptyStateAll onCreate={() => setCreateStep("explainer")} />
					) : (
						<div className="space-y-8 sm:space-y-10">
							<Section
								title={t("selectOrganization.ownedTitle")}
								items={ownedOrgs}
								subscriptions={subscriptions}
								testId="owned-organizations-section"
								onRequestDelete={handleOpenDeleteDialog}
								ownerControls
								emptyText={t("selectOrganization.emptyOwned")}
							/>
							<Section
								title={t("selectOrganization.invitedTitle")}
								items={invitedOrgs}
								testId="invited-organizations-section"
								ownerControls={false}
								emptyText={t("selectOrganization.emptyInvited")}
							/>
						</div>
					)}
				</div>
			</div>

			{/* Onboarding Modal - shown after organization creation */}
			{createdOrg && (
				<OnboardingModal
					isOpen={isOnboardingOpen}
					onClose={() => {
						setIsOnboardingOpen(false);
						setCreatedOrg(null);
					}}
					organizationName={createdOrg.name}
					organizationId={createdOrg.id}
				/>
			)}

			{/* Delete Organization Confirmation Dialog */}
			<Dialog
				open={!!orgToDelete}
				onOpenChange={(open) => {
					if (!open) {
						handleCloseDeleteDialog();
					}
				}}
			>
				<DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
					<DialogHeader>
						<DialogTitle className="flex items-center gap-2 text-error">
							<AlertTriangle className="h-5 w-5" />
							{t("selectOrganization.deleteDialog.title")}
						</DialogTitle>
						<DialogDescription>
							{t("selectOrganization.deleteDialog.description", {
								organizationName: orgToDelete?.name ?? "",
							})}
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-4 py-4">
						<p className="text-sm font-semibold text-error">
							{t("selectOrganization.deleteDialog.irreversibleWarning")}
						</p>
						<div className="rounded-lg border border-warning/30 bg-warning/10 p-3 text-sm text-warning">
							{t("selectOrganization.deleteDialog.dataLossWarning")}
						</div>
						<div className="space-y-2">
							<Label htmlFor={confirmOrgNameId}>
								{t("selectOrganization.deleteDialog.confirmLabel", {
									organizationName: orgToDelete?.name ?? "",
								})}
							</Label>
							<Input
								id={confirmOrgNameId}
								value={deleteOrgNameConfirm}
								onChange={(e) => setDeleteOrgNameConfirm(e.target.value)}
								autoComplete="off"
								data-testid="delete-organization-confirm-input"
							/>
						</div>
					</div>
					<DialogFooter>
						<button
							type="button"
							className="btn btn-outline"
							onClick={handleCloseDeleteDialog}
							disabled={deleteOrganizationMutation.isPending}
						>
							{t("common.cancel")}
						</button>
						<button
							type="button"
							className="btn btn-error text-error-content"
							onClick={handleConfirmDeleteOrganization}
							disabled={
								!orgToDelete ||
								deleteOrgNameConfirm.trim() !== (orgToDelete?.name ?? "") ||
								deleteOrganizationMutation.isPending
							}
							data-testid="delete-organization-confirm-button"
						>
							{deleteOrganizationMutation.isPending
								? t("selectOrganization.deleteDialog.deleting")
								: t("selectOrganization.deleteDialog.confirmButton")}
						</button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</AppBaseLayout>
	);
}

function PageHeader({
	hasAnyOrgs,
	onCreate,
	showExplainerTrigger,
	onOpenExplainer,
}: {
	hasAnyOrgs: boolean;
	onCreate: () => void;
	showExplainerTrigger: boolean;
	onOpenExplainer: () => void;
}) {
	const { t } = useTranslation();
	return (
		<div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
			<div className="space-y-1 sm:space-y-2 max-w-2xl">
				<h1
					data-testid="page-title"
					className="text-2xl sm:text-3xl font-bold text-base-content tracking-tight"
				>
					{t("selectOrganization.title")}
				</h1>
				<p className="text-sm sm:text-base text-base-content/70">
					{t("selectOrganization.subtitle")}
				</p>
				{showExplainerTrigger && (
					<button
						type="button"
						onClick={onOpenExplainer}
						data-testid="org-explainer-trigger"
						className="mt-1 inline-flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 transition-colors underline decoration-dotted underline-offset-4 decoration-primary/40 hover:decoration-primary/70"
					>
						<Info className="h-3.5 w-3.5" />
						{t("selectOrganization.explainer.title")}
					</button>
				)}
			</div>
			{hasAnyOrgs && (
				<Button
					className="btn btn-primary text-primary-content w-full sm:w-auto shrink-0"
					onClick={onCreate}
				>
					{t("organization.createNew")}
				</Button>
			)}
		</div>
	);
}

function OrgExplainerPanel({ onClose }: { onClose: () => void }) {
	const { t } = useTranslation();
	return (
		<aside
			data-testid="org-explainer"
			className="relative rounded-xl border border-base-300 bg-base-200/60 p-4 sm:p-5"
		>
			<div className="flex items-start gap-3 sm:gap-4 pr-8">
				<div className="mt-0.5 shrink-0 rounded-lg bg-primary/10 p-2 text-primary">
					<Info className="h-4 w-4 sm:h-5 sm:w-5" />
				</div>
				<div className="min-w-0 flex-1 space-y-2">
					<h2 className="text-sm sm:text-base font-semibold text-base-content">
						{t("selectOrganization.explainer.title")}
					</h2>
					<div className="space-y-2 text-sm text-base-content/80 leading-relaxed">
						<p>{t("selectOrganization.explainer.definition")}</p>
						<p>{t("selectOrganization.explainer.rule")}</p>
						<p>{t("selectOrganization.explainer.newOrg")}</p>
					</div>
				</div>
			</div>
			<button
				type="button"
				onClick={onClose}
				aria-label={t("selectOrganization.explainer.hide")}
				className="absolute top-3 right-3 rounded-md p-1 text-base-content/50 hover:bg-base-300 hover:text-base-content transition-colors"
			>
				<X className="h-4 w-4" />
			</button>
		</aside>
	);
}

function EmptyStateAll({ onCreate }: { onCreate: () => void }) {
	const { t } = useTranslation();
	return (
		<div className="mt-2 sm:mt-4 rounded-2xl border border-base-300 bg-base-200/40 p-6 sm:p-10 text-center space-y-4">
			<div className="mx-auto w-14 h-14 rounded-full bg-primary/10 text-primary flex items-center justify-center">
				<Building2 className="h-7 w-7" />
			</div>
			<div className="space-y-1.5 max-w-md mx-auto">
				<h3 className="text-lg sm:text-xl font-semibold text-base-content">
					{t("organization.noOrganizations")}
				</h3>
				<p className="text-sm sm:text-base text-base-content/70">
					{t("organization.noOrganizationsDescription")}
				</p>
			</div>
			<div className="pt-2">
				<Button
					className="btn btn-primary text-primary-content"
					onClick={onCreate}
				>
					{t("organization.createFirst")}
				</Button>
			</div>
		</div>
	);
}

function Section({
	title,
	items,
	subscriptions,
	testId,
	onRequestDelete,
	ownerControls,
	emptyText,
}: {
	title: string;
	items: API_UserOrganization[];
	subscriptions?: Record<string, OrgSub>;
	testId?: string;
	onRequestDelete?: (org: API_UserOrganization) => void;
	ownerControls: boolean;
	emptyText?: string;
}) {
	const { t } = useTranslation();

	return (
		<section className="space-y-3 sm:space-y-4" data-testid={testId}>
			<header className="flex items-center gap-2">
				<h2 className="text-base sm:text-lg font-semibold text-base-content flex items-center gap-2">
					{ownerControls ? (
						<ShieldCheck className="h-4 w-4 text-primary" />
					) : (
						<Mail className="h-4 w-4 text-accent" />
					)}
					{title}
				</h2>
				<span className="text-xs sm:text-sm text-base-content/50" aria-hidden>
					·
				</span>
				<span className="text-xs sm:text-sm text-base-content/60">
					{t("selectOrganization.countLabel", { count: items.length })}
				</span>
			</header>

			{items.length === 0 ? (
				<div className="rounded-xl border border-dashed border-base-300 bg-base-200/30 p-4 sm:p-6 text-center text-sm text-base-content/70">
					{emptyText}
				</div>
			) : (
				<div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
					{items.map((org) => (
						<OrgCard
							key={org.id}
							org={org}
							sub={subscriptions?.[org.id]}
							onRequestDelete={
								ownerControls && onRequestDelete
									? () => onRequestDelete(org)
									: undefined
							}
						/>
					))}
				</div>
			)}
		</section>
	);
}

function OrgCard({
	org,
	sub,
	onRequestDelete,
}: {
	org: API_UserOrganization;
	sub?: OrgSub;
	onRequestDelete?: () => void;
}) {
	const { t } = useTranslation();

	const kind = sub ? deriveStatusKind(sub) : null;

	// Route billing-problem states straight to the renewal flow so the primary
	// CTA lands the owner where they can fix it, otherwise open the organization.
	const goesToRenew =
		kind === "setup_required" ||
		kind === "payment_required" ||
		kind === "past_due" ||
		kind === "inactive";

	const actionLabel =
		kind === "setup_required"
			? t("selectOrganization.action.choosePlan")
			: kind === "payment_required" || kind === "past_due"
				? t("selectOrganization.action.updatePayment")
				: kind === "inactive"
					? t("selectOrganization.action.reactivate")
					: t("selectOrganization.action.open");

	const cardBody = (
		<Card
			data-testid="organization-card"
			className="group h-full flex flex-col overflow-hidden border border-base-300 bg-base-100 transition-all duration-200 hover:border-primary/60 hover:shadow-md hover:-translate-y-0.5"
		>
			<CardContent className="flex-1 p-4 sm:p-5 flex flex-col gap-3.5">
				<div className="flex items-start justify-between gap-2">
					<div className="flex items-center gap-2.5 min-w-0">
						<span className="shrink-0 rounded-lg bg-primary/10 p-2 text-primary">
							<Building2 className="h-4 w-4" />
						</span>
						<div className="min-w-0">
							<h3
								data-testid="organization-name"
								className="text-base sm:text-[17px] font-semibold text-base-content leading-snug line-clamp-2 break-words"
							>
								{org.name}
							</h3>
							<p
								data-testid="organization-role"
								className="text-xs text-base-content/60 mt-0.5"
							>
								{getOrganizationRoleLabel(org.role as Role, t)}
							</p>
						</div>
					</div>
					{onRequestDelete && (
						<button
							type="button"
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
								onRequestDelete();
							}}
							data-testid="delete-organization-button"
							aria-label={t("selectOrganization.deleteButton")}
							title={t("selectOrganization.deleteButton")}
							className="shrink-0 rounded-md p-1.5 text-base-content/40 hover:bg-error/10 hover:text-error transition-colors opacity-70 hover:opacity-100"
						>
							<Trash2 className="h-4 w-4" />
						</button>
					)}
				</div>

				{org.description && (
					<p className="text-xs sm:text-sm text-base-content/60 line-clamp-2 break-words">
						{org.description}
					</p>
				)}

				<div className="flex-1" />

				{sub && kind && <StatusRow kind={kind} sub={sub} />}
			</CardContent>

			<div className="border-t border-base-300 bg-base-200/40 p-3 sm:p-4">
				<Button
					data-testid="select-organization-button"
					className="w-full justify-center gap-2 btn btn-primary text-primary-content"
				>
					{actionLabel}
					<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
				</Button>
			</div>
		</Card>
	);

	if (goesToRenew) {
		return (
			<Link
				to="/app/org/renew-subscription"
				search={{ orgId: org.id }}
				className="cursor-pointer block h-full"
			>
				{cardBody}
			</Link>
		);
	}

	return (
		<Link
			to="/app/org/$orgId"
			params={{ orgId: org.id }}
			className="cursor-pointer block h-full"
		>
			{cardBody}
		</Link>
	);
}

function StatusRow({ kind, sub }: { kind: StatusKind; sub: OrgSub }) {
	const { t } = useTranslation();

	if (kind === "checking") {
		return (
			<div className="flex items-center gap-2 text-xs text-base-content/50">
				<span className="h-1.5 w-1.5 rounded-full bg-base-300 animate-pulse" />
				{t("selectOrganization.status.checking")}
			</div>
		);
	}

	const trialDays = kind === "trialing" ? trialDaysLeft(sub.trialEndsAt) : null;

	const config: Record<
		Exclude<StatusKind, "checking">,
		{ dot: string; text: string; label: string }
	> = {
		active: {
			dot: "bg-success",
			text: "text-success",
			label: t("selectOrganization.status.active"),
		},
		trialing: {
			dot: "bg-accent",
			text: "text-accent",
			label:
				trialDays !== null
					? t("selectOrganization.status.trialingWithDays", {
							count: trialDays,
						})
					: t("selectOrganization.status.trialing"),
		},
		setup_required: {
			dot: "bg-warning",
			text: "text-warning",
			label: t("selectOrganization.status.setupRequired"),
		},
		payment_required: {
			dot: "bg-error",
			text: "text-error",
			label: t("selectOrganization.status.paymentRequired"),
		},
		past_due: {
			dot: "bg-error",
			text: "text-error",
			label: t("selectOrganization.status.pastDue"),
		},
		inactive: {
			dot: "bg-base-content/40",
			text: "text-base-content/60",
			label: t("selectOrganization.status.inactive"),
		},
		unknown: {
			dot: "bg-base-content/30",
			text: "text-base-content/50",
			label: t("selectOrganization.status.unknown"),
		},
	};

	const { dot, text, label } = config[kind];

	return (
		<div className="flex items-center gap-2 min-w-0">
			<span className={cn("h-2 w-2 rounded-full shrink-0", dot)} aria-hidden />
			<span className={cn("text-xs sm:text-sm font-medium truncate", text)}>
				{label}
			</span>
			{sub.planName && kind !== "unknown" && (
				<>
					<span className="text-base-content/30 text-xs shrink-0" aria-hidden>
						·
					</span>
					<Badge
						variant="outline"
						className="border-base-300 bg-base-200/60 text-[10px] sm:text-[11px] font-medium px-2 py-0 shrink-0"
					>
						{sub.planName}
					</Badge>
				</>
			)}
		</div>
	);
}

type CreateStep = "explainer" | "form" | null;

function CreateOrganizationFlow({
	step,
	onStepChange,
	onClose,
	orgNameId,
	orgDescriptionId,
	orgCurrencyId,
	name,
	description,
	currency,
	onNameChange,
	onDescriptionChange,
	onCurrencyChange,
	onSubmit,
	isSubmitting,
}: {
	step: CreateStep;
	onStepChange: (step: CreateStep) => void;
	onClose: () => void;
	orgNameId: string;
	orgDescriptionId: string;
	orgCurrencyId: string;
	name: string;
	description: string;
	currency: Currency;
	onNameChange: (v: string) => void;
	onDescriptionChange: (v: string) => void;
	onCurrencyChange: (v: Currency) => void;
	onSubmit: () => void;
	isSubmitting: boolean;
}) {
	const { t } = useTranslation();

	const isExplainer = step === "explainer";
	const isForm = step === "form";

	return (
		<Dialog
			open={step !== null}
			onOpenChange={(open) => {
				if (!open) onClose();
			}}
		>
			<DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto mx-2 sm:mx-auto">
				<DialogHeader className="space-y-2">
					<DialogTitle className="text-xl font-semibold text-base-content flex items-center gap-2">
						<Building2 className="h-5 w-5 text-primary" />
						{isExplainer
							? t("organization.createExplainer.title")
							: t("organization.createNew")}
					</DialogTitle>
					{isForm && (
						<DialogDescription className="text-base-content/70">
							{t("organization.createDescription")}
						</DialogDescription>
					)}
				</DialogHeader>

				<StepProgress currentStep={isExplainer ? 1 : 2} totalSteps={2} />

				{isExplainer ? (
					<ExplainerStepContent />
				) : (
					<FormStepContent
						orgNameId={orgNameId}
						orgDescriptionId={orgDescriptionId}
						orgCurrencyId={orgCurrencyId}
						name={name}
						description={description}
						currency={currency}
						onNameChange={onNameChange}
						onDescriptionChange={onDescriptionChange}
						onCurrencyChange={onCurrencyChange}
					/>
				)}

				<DialogFooter>
					{isExplainer ? (
						<>
							<button
								type="button"
								className="btn btn-outline"
								onClick={onClose}
							>
								{t("common.cancel")}
							</button>
							<button
								type="button"
								className="btn btn-primary gap-2"
								onClick={() => onStepChange("form")}
								data-testid="create-org-continue-button"
							>
								{t("common.continue")}
								<ArrowRight className="h-4 w-4" />
							</button>
						</>
					) : (
						<>
							<button
								type="button"
								className="btn btn-ghost gap-2"
								onClick={() => onStepChange("explainer")}
								data-testid="create-org-back-button"
							>
								<ArrowLeft className="h-4 w-4" />
								{t("common.back")}
							</button>
							<button
								type="button"
								className="btn btn-primary"
								onClick={onSubmit}
								disabled={!name.trim() || isSubmitting}
							>
								{isSubmitting ? t("common.creating") : t("common.create")}
							</button>
						</>
					)}
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

function StepProgress({
	currentStep,
	totalSteps,
}: {
	currentStep: number;
	totalSteps: number;
}) {
	const { t } = useTranslation();
	return (
		<div className="flex items-center gap-3 pt-1 pb-4 border-b border-base-300 mb-1">
			<div className="flex items-center gap-1.5">
				{Array.from({ length: totalSteps }).map((_, i) => {
					const stepNumber = i + 1;
					const isActive = stepNumber === currentStep;
					const isDone = stepNumber < currentStep;
					return (
						<span
							key={`step-${stepNumber}`}
							className={cn(
								"h-1.5 rounded-full transition-all duration-300",
								isActive
									? "w-8 bg-primary"
									: isDone
										? "w-4 bg-primary/60"
										: "w-4 bg-base-300",
							)}
						/>
					);
				})}
			</div>
			<span className="text-xs text-base-content/60">
				{t("onboarding.stepOf", { current: currentStep, total: totalSteps })}
			</span>
		</div>
	);
}

function ExplainerStepContent() {
	const { t } = useTranslation();
	return (
		<div className="flex items-start gap-3">
			<div className="shrink-0 rounded-xl bg-primary/10 p-3 text-primary">
				<Building2 className="h-5 w-5 sm:h-6 sm:w-6" />
			</div>
			<div className="min-w-0 flex-1 space-y-3 pt-1 text-sm sm:text-base text-base-content/80 leading-relaxed">
				<p>{t("organization.createExplainer.definition")}</p>
				<p>{t("organization.createExplainer.contents")}</p>
				<p>{t("organization.createExplainer.rule")}</p>
				<p>{t("organization.createExplainer.newOrgWhen")}</p>
				<p>{t("organization.createExplainer.newOrgWhenNot")}</p>
			</div>
		</div>
	);
}

function FormStepContent({
	orgNameId,
	orgDescriptionId,
	orgCurrencyId,
	name,
	description,
	currency,
	onNameChange,
	onDescriptionChange,
	onCurrencyChange,
}: {
	orgNameId: string;
	orgDescriptionId: string;
	orgCurrencyId: string;
	name: string;
	description: string;
	currency: Currency;
	onNameChange: (v: string) => void;
	onDescriptionChange: (v: string) => void;
	onCurrencyChange: (v: Currency) => void;
}) {
	const { t } = useTranslation();

	return (
		<div className="grid gap-5">
			<div className="grid gap-1.5">
				<Label htmlFor={orgNameId}>{t("organization.name")}</Label>
				<Input
					id={orgNameId}
					value={name}
					onChange={(e) => onNameChange(e.target.value)}
					placeholder={t("organization.namePlaceholder")}
					maxLength={100}
					autoFocus
				/>
				<p className="text-xs text-base-content/60">
					{t("organization.nameHelp")}
				</p>
			</div>
			<div className="grid gap-1.5">
				<Label htmlFor={orgDescriptionId}>
					{t("organization.description")}
				</Label>
				<Textarea
					id={orgDescriptionId}
					value={description}
					onChange={(e) => onDescriptionChange(e.target.value)}
					placeholder={t("organization.descriptionPlaceholder")}
					maxLength={500}
					rows={3}
					className="min-h-20 resize-none"
				/>
				<p className="text-xs text-base-content/60">
					{t("organization.descriptionHelp")}
				</p>
			</div>
			<div className="grid gap-1.5">
				<Label htmlFor={orgCurrencyId}>
					{t("organization.currency.label")}
				</Label>
				<Select
					value={currency}
					onValueChange={(value) => onCurrencyChange(value as Currency)}
				>
					<SelectTrigger id={orgCurrencyId}>
						<SelectValue placeholder={t("organization.currency.placeholder")} />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value={Currency.BRL}>
							{t("organization.currency.brl")}
						</SelectItem>
						<SelectItem value={Currency.USD}>
							{t("organization.currency.usd")}
						</SelectItem>
					</SelectContent>
				</Select>
				<div className="flex items-start gap-2 rounded-lg border border-warning/30 bg-warning/10 p-3 text-xs sm:text-sm text-warning">
					<Info className="mt-0.5 h-4 w-4 shrink-0" />
					<span>{t("organization.currency.warning")}</span>
				</div>
			</div>
		</div>
	);
}
